import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress
} from '@mui/material';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import { assistantService, authService } from '../utils/api';
import { TTS_LANGUAGE_OPTIONS, TTS_MODEL_OPTIONS, TTS_PROVIDER_OPTIONS } from '../utils/ttsOptions';

function TextToSpeech() {
  const { assistantId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assistant, setAssistant] = useState(null);
  const [form, setForm] = useState({
    tts_provider: '',
    tts_model: '',
    tts_voice_id: '',
    tts_language: '',
    tts_speed: ''
  });

  const userRole = authService.getUserRole();
  const canEdit = userRole === 'super_admin' || userRole === 'admin';

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const data = await assistantService.getAssistantById(assistantId);
        if (!isMounted) return;
        setAssistant(data);
        setForm({
          tts_provider: data.tts_provider || 'cartesia',
          tts_model: data.tts_model || '',
          tts_voice_id: data.tts_voice_id || '',
          tts_language: data.tts_language || '',
          tts_speed: data.tts_speed ?? ''
        });
      } catch (e) {
        toast.error(e?.message || 'Failed to load assistant TTS settings');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    if (assistantId) load();
    return () => {
      isMounted = false;
    };
  }, [assistantId]);

  const title = useMemo(() => assistant?.assistant_name || assistant?.name || 'Assistant', [assistant]);

  const handleChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        tts_provider: form.tts_provider || undefined,
        tts_model: form.tts_model || undefined,
        tts_voice_id: form.tts_voice_id?.trim() || undefined,
        tts_language: form.tts_language || undefined,
        tts_speed: form.tts_speed === '' ? undefined : Number(form.tts_speed)
      };
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

      const updated = await assistantService.updateAssistant(assistantId, payload);
      toast.success('TTS settings saved');

      // keep session selected assistant in sync (nice for header)
      const selected = assistantService.getSelectedAssistant();
      if (selected?.id === assistantId) {
        assistantService.setSelectedAssistant({ ...selected, ...updated });
      }
    } catch (e) {
      toast.error(e?.message || e?.detail || 'Failed to save TTS settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', p: 3 }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
              Text to Speech
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              {title}
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Card elevation={0}>
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" disabled>
                      <InputLabel>TTS Provider</InputLabel>
                      <Select
                        value={form.tts_provider}
                        label="TTS Provider"
                        onChange={(e) => handleChange('tts_provider', e.target.value)}
                      >
                        {TTS_PROVIDER_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" disabled>
                      <InputLabel>TTS Model</InputLabel>
                      <Select
                        value={form.tts_model}
                        label="TTS Model"
                        onChange={(e) => handleChange('tts_model', e.target.value)}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {TTS_MODEL_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="TTS Voice ID"
                      value={form.tts_voice_id}
                      onChange={(e) => handleChange('tts_voice_id', e.target.value)}
                      disabled={!canEdit}
                      placeholder="e.g. voice_abc123"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" disabled>
                      <InputLabel>TTS Language</InputLabel>
                      <Select
                        value={form.tts_language}
                        label="TTS Language"
                        onChange={(e) => handleChange('tts_language', e.target.value)}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {TTS_LANGUAGE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="TTS Speed"
                      value={form.tts_speed}
                      onChange={(e) => handleChange('tts_speed', e.target.value)}
                      disabled={!canEdit}
                      inputProps={{ min: 0, step: 0.1 }}
                      placeholder="e.g. 1.0"
                    />
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!canEdit || saving}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </Box>

                {!canEdit && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', mt: 1, display: 'block' }}>
                    View-only access. Contact an admin to edit.
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}
        </Container>
      </Box>
    </DashboardLayout>
  );
}

export default TextToSpeech;

